'use client';

import { Button } from '@/components/atoms';
import { useState } from 'react';

import { SubPlayerModal } from '@/components/organisms/ScoreCards/subs/SubPlayerModal';
import { Matches, TeamLeaguePlayerInfo } from '@/entities';
import { RotationsInfo } from '@/entities/rotations/RotationsInfo';
import SubsListSelectModal from '@/components/organisms/ScoreCards/subs/SubsListSelectModal';
import { MatchupStatusEnum } from '@/domain/constants/constants';
import { useTeamLeagueStore } from '@/store/team-league-store';
import { useCountriesQuery } from '@/hooks/lookups/useCountriesQuery';
import { useStatesQuery } from '@/hooks/lookups/useStatesQuery';

interface ScoreCardsLayoutProps {
	children: React.ReactNode;
	displaySubBtn?: boolean;
	title: string;
	matchupData?: Matches;
	rotationsInfo?: RotationsInfo;
	teamOnePlayers?: TeamLeaguePlayerInfo[];
	teamTwoPlayers?: TeamLeaguePlayerInfo[];
	substitutePlayers?: TeamLeaguePlayerInfo[];
	hideEditLineup?: boolean;
}

export const ScoreCardsLayout = ({
	children,
	displaySubBtn = false,
	title,
	matchupData,
	rotationsInfo,
	teamOnePlayers = [],
	teamTwoPlayers = [],
	substitutePlayers = [],
	hideEditLineup
}: ScoreCardsLayoutProps) => {
	const teamLeagueStore = useTeamLeagueStore();

	const [openSubsListSelect, setOpenSubsListSelect] = useState(false);
	const [openSubPlayerModal, setOpenSubPlayerModal] = useState(false);
	const [selectedSubsList, setSelectedSubsList] = useState<'team' | 'event' | 'users'>('team');

	const { data: countries } = useCountriesQuery();
	const { data: states } = useStatesQuery();

	const isSubstitutionAllowed = (): boolean => {
		const isInProgressOrReadyToStart =
			matchupData?.matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ||
			matchupData?.matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS;

		if (!isInProgressOrReadyToStart) {
			return false;
		}
		return true;
	};

	return (
		<div className="flex w-full flex-col">
			<div className="py-9">
				<div className="w-full flex justify-between items-center">
					<h2 className="text-base font-semibold text-gray-900 md:text-2xl">{title}</h2>
					{displaySubBtn && isSubstitutionAllowed() && !hideEditLineup && (
						<Button
							className="text-sm font-semibold leading-5 text-gray-700 border border-x border-gray-300 !px-3.5 bg-white !py-2 hover:!bg-gray-25 hover:!border-gray-400"
							onClick={() => {
								setOpenSubsListSelect(true);
							}}
						>
							Substitute
						</Button>
					)}
				</div>
				{children}
			</div>

			{openSubsListSelect && (
				<SubsListSelectModal
					isOpen={openSubsListSelect}
					setIsOpen={setOpenSubsListSelect}
					onSelect={(list: 'team' | 'event' | 'users') => {
						setOpenSubsListSelect(false);
						setOpenSubPlayerModal(true);
						setSelectedSubsList(list);
					}}
					useScheduleGroups={teamLeagueStore.useScheduleGroups}
				/>
			)}

			<SubPlayerModal
				rotationsInfo={rotationsInfo}
				open={openSubPlayerModal}
				onOpenChange={setOpenSubPlayerModal}
				type={selectedSubsList}
				teamOnePlayers={teamOnePlayers}
				teamTwoPlayers={teamTwoPlayers}
				teamOneTitle={matchupData?.teamOneTitle ?? ''}
				teamTwoTitle={matchupData?.teamTwoTitle ?? ''}
				substitutePlayers={substitutePlayers}
				states={states?.data?.results || []}
				countries={countries?.data?.results || []}
				matches={matchupData?.matches ?? []}
				matchupUuid={matchupData?.uuid ?? ''}
				matchupData={matchupData}
			/>
		</div>
	);
};
